// Provenance: repo:tds-reconciliation-portal:frontend/src/components/RunSummary.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Run ids became UUID strings; the
// NON_ACTIONABLE set, category geometry and provenance strip carry unchanged.
import { useResolutionProgress } from '../api/resolutions'
import type { ResolutionProgress, Run } from '../api/types'
import { CATEGORIES, categoryMeta, fmtInt, shortSha } from '../format'
import { CategoryBadge, Chip, type Tone } from './badges'
import { Money } from './Money'
import { TieOutBanner } from './TieOutBanner'

const NON_ACTIONABLE = new Set(['Matched - agreed', 'In 26AS with NIL TDS (informational)'])

const TONE_BORDER: Record<Tone, string> = {
  gray: 'border-t-muted-foreground',
  blue: 'border-t-blue-500',
  green: 'border-t-emerald-500',
  yellow: 'border-t-amber-400',
  red: 'border-t-red-500',
  orange: 'border-t-orange-500',
  violet: 'border-t-violet-500',
  teal: 'border-t-teal-500',
}

function ProgressCard({ progress }: { progress: ResolutionProgress }) {
  const actionable = Object.entries(progress.categories).filter(([c]) => !NON_ACTIONABLE.has(c))
  const totals = actionable.reduce(
    (acc, [, v]) => ({
      items: acc.items + v.items,
      approved: acc.approved + v.approved,
      pending: acc.pending + v.pending,
      open: acc.open + v.open,
    }),
    { items: 0, approved: 0, pending: 0, open: 0 },
  )
  const pct = (n: number) => (totals.items > 0 ? (n / totals.items) * 100 : 0)
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-2 flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold text-foreground">Resolution progress</h3>
        {progress.reconciliation_complete ? (
          <Chip tone="green" solid>RECONCILIATION COMPLETE</Chip>
        ) : (
          <p className="text-sm text-muted-foreground">
            {fmtInt(totals.approved)} approved · {fmtInt(totals.pending)} pending ·{' '}
            {fmtInt(totals.open)} open of {fmtInt(totals.items)} actionable parties
          </p>
        )}
      </div>
      <div className="flex h-4 overflow-hidden rounded bg-muted text-[10px] font-medium text-white">
        <div className="flex items-center justify-center bg-emerald-500"
          style={{ width: `${pct(totals.approved)}%` }} title={`${totals.approved} approved`}>
          {pct(totals.approved) > 12 ? 'approved' : ''}
        </div>
        <div className="flex items-center justify-center bg-amber-400"
          style={{ width: `${pct(totals.pending)}%` }} title={`${totals.pending} pending`}>
          {pct(totals.pending) > 12 ? 'pending' : ''}
        </div>
        <div className="flex items-center justify-center bg-transparent text-muted-foreground"
          style={{ width: `${pct(totals.open)}%` }} title={`${totals.open} open`}>
          {pct(totals.open) > 12 ? 'open' : ''}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-6">
        {actionable.map(([cat, v]) => (
          <div key={cat}>
            <CategoryBadge category={cat} />
            <p className="mt-0.5 text-xs text-muted-foreground">
              {fmtInt(v.approved)}/{fmtInt(v.items)} approved
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function CategoryCards({ run }: { run: Run }) {
  const cats = run.control_totals?.categories ?? {}
  const keys = [
    ...CATEGORIES.filter((c) => c in cats),
    ...Object.keys(cats).filter((c) => !CATEGORIES.includes(c)),
  ]
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {keys.map((cat) => {
        const v = cats[cat]
        const meta = categoryMeta(cat)
        return (
          <div
            key={cat}
            className={`rounded-lg border border-border border-t-[3px] p-3 ${TONE_BORDER[meta.color as Tone] ?? 'border-t-muted-foreground'}`}
          >
            <div className="space-y-1">
              <CategoryBadge category={cat} />
              <p className="text-xl font-bold text-foreground">{fmtInt(v.n)}</p>
              <div>
                <p className="text-xs text-muted-foreground">Σ difference</p>
                <Money v={v.diff} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** The completed-run dashboard: tie-out, categories, resolution progress,
 *  transaction stats, warnings and provenance. Hosted in the run-details
 *  drawer so one surface carries the whole run. */
export function RunSummary({ run, tolerated }: {
  run: Run
  tolerated?: number
}) {
  const progress = useResolutionProgress(run.id, run.status === 'complete')
  const ct = run.control_totals
  const prov = ct?.provenance
  if (!ct) return null

  return (
    <div className="space-y-4">
      <TieOutBanner ct={ct} />

      {run.book_grain === 'party' && (
        <p className="text-sm font-semibold text-orange-800">
          Books provided party-level sums only — matched/open is stated per
          party, not per transaction.
        </p>
      )}

      {(run.tolerance || tolerated) && (
        <div className="flex flex-wrap items-center gap-2">
          {run.tolerance && (
            <Chip tone="yellow">Tolerance ₹{Number(run.tolerance).toLocaleString('en-IN')}</Chip>
          )}
          {tolerated ? (
            <p className="text-sm text-muted-foreground">
              {fmtInt(tolerated)} part{tolerated === 1 ? 'y is' : 'ies are'} agreed within
              tolerance — accepted, and flagged <b>TOL</b> below.
            </p>
          ) : null}
        </div>
      )}

      <h3 className="text-base font-semibold text-foreground">Categories</h3>
      <CategoryCards run={run} />

      {progress.data && <ProgressCard progress={progress.data} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Transaction matching</h3>
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-xs text-muted-foreground">Match groups</p>
              <p className="font-bold text-foreground">{fmtInt(ct.txn_match_groups)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Resolutions carried / orphaned</p>
              <p className="font-bold text-foreground">
                {fmtInt(ct.carry_forward.carried)} / {fmtInt(ct.carry_forward.orphaned)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Ledger Cr (info)</p>
              <Money v={ct.ledger_cr} />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Warnings</h3>
            <Chip tone={(run.warnings?.length ?? 0) > 0 ? 'orange' : 'green'}>
              {run.warnings?.length ?? 0}
            </Chip>
          </div>
          {(run.warnings?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No validation warnings.</p>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {run.warnings?.map((w, i) => (
                <p key={i} className="text-sm text-orange-900">• {w}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {prov && (
        <div className="rounded-lg bg-muted p-4">
          <p className="text-xs text-muted-foreground">
            <b>Provenance</b> · engine {prov.engine_version} · FY {prov.fy_start} → {prov.fy_end} ·
            26AS sha256 {shortSha(prov.f26as.sha256)}… · ledger sha256{' '}
            {shortSha(prov.ledger.sha256)}… · alias groups {prov.alias_map.applied ?? 0}/
            {prov.alias_map.groups ?? 0} applied (sha256 {shortSha(prov.alias_map.sha256)}…)
          </p>
        </div>
      )}
    </div>
  )
}
